import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import AdminCrud from "@/pages/admin/components/AdminCrud";
import type { CrudField } from "@/pages/admin/components/AdminCrud";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition";

const pointFields: CrudField[] = [
  { key: "title", label: "Titre", required: true },
  { key: "description", label: "Description", type: "textarea" },
  { key: "icon", label: "Icône", type: "icon" },
];

export default function AdminMission() {
  const [title, setTitle] = useState("");
  const [intro, setIntro] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [isError, setIsError] = useState(false);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("mission_content")
      .select("*")
      .eq("id", 1)
      .maybeSingle();
    if (!error && data) {
      setTitle(data.title ?? "");
      setIntro(data.intro ?? "");
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setIsError(false);

    const { error } = await supabase
      .from("mission_content")
      .upsert({ id: 1, title, intro });

    setSaving(false);
    if (error) {
      setMsg(error.message);
      setIsError(true);
      return;
    }
    setMsg("Énoncé de mission enregistré.");
  }

  return (
    <div className="p-6 md:p-10">
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
        Notre mission
      </h1>
      <p className="mt-2 text-sm md:text-base text-foreground-600">
        Le texte de présentation et les piliers de la mission, affichés sur la
        page « L'association ».
      </p>

      {loading ? (
        <div className="mt-10 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : (
        <>
          {msg && (
            <div
              className={`mt-6 rounded-md px-4 py-3 text-sm ${
                isError
                  ? "bg-accent-100 text-accent-700"
                  : "bg-primary-100 text-primary-700"
              }`}
            >
              {msg}
            </div>
          )}

          <form
            onSubmit={handleSave}
            className="mt-6 bg-background-50 rounded-lg border border-background-200 p-6 md:p-8 space-y-5"
          >
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Titre
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Texte d'introduction
              </label>
              <textarea
                rows={3}
                value={intro}
                onChange={(e) => setIntro(e.target.value)}
                className={inputClass}
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-8 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enregistrement…
                </>
              ) : (
                <>
                  <i className="ri-save-line"></i>
                  Enregistrer
                </>
              )}
            </button>
          </form>

          <div className="mt-8">
            <AdminCrud
              title="Piliers de la mission"
              description="Les points clés listés sous l'énoncé de mission."
              addLabel="Ajouter un pilier"
              table="mission_points"
              fields={pointFields}
              listTitleKey="title"
              listIconKey="icon"
            />
          </div>
        </>
      )}
    </div>
  );
}